import React, {useState, useEffect, FunctionComponent} from "react";
import { useNavigate } from "react-router-dom";
import { getCurrentUser } from "../services/firebase/auth";
import { subscribeToBellNotificationCount } from "../services/firebase/firestore/notificationServices";

import { NeumorphicButton } from "../components/web/NeumorphicButton";
import { IconButton } from "../components/web/IconButton";
import { WelcomeUser } from "../components/web/WelcomeUser";

// Import raw GoBell and IconBaseProps
import { GoBell as GoBellRawImport } from "react-icons/go";
import { IconBaseProps } from "react-icons"; // Correct import from root 'react-icons'

// Assert the raw GoBell import to FunctionComponent
const GoBell: FunctionComponent<IconBaseProps> = GoBellRawImport as unknown as FunctionComponent<IconBaseProps>;

const Home: React.FC = () => {
  const navigate = useNavigate();
  const currentUserUid = getCurrentUser()?.uid;
  const [unreadNotificationCount, setUnreadNotificationCount] = useState<number>(0);

  useEffect(() => {
    if (!currentUserUid) {
      setUnreadNotificationCount(0);
      return;
    }

    const unsubscribe = subscribeToBellNotificationCount(
      currentUserUid,
      (newCount) => {
        setUnreadNotificationCount(newCount);
      },
      (error) => {
        // eslint-disable-next-line no-console
        console.error("Error fetching unread notification count on home:", error);
        setUnreadNotificationCount(0);
      },
    );

    return () => unsubscribe();
  }, [currentUserUid]);

  return (
    <div className="home-dashboard">
      <header className="dashboard-header">
        <WelcomeUser
          // You can pass an onClick handler to navigate to a user profile page
          onClick={() => navigate("/profile")}
          className="your-custom-welcome-user-class" // Add specific styling if needed
        />
        <IconButton
          isNeumorphic={true} // ensure this prop has a value
          onPress={() => navigate("/notifications")}
          className="util-mr-s"
        >
          <div className="bell-icon-wrapper">
            <GoBell size={24} color="black" /> {/* Ensure size and color have values*/}
            {unreadNotificationCount > 0 && (
              <div className="bell-badge-container">
                {unreadNotificationCount > 9 ? "9+" : unreadNotificationCount}
              </div>
            )}
          </div>
        </IconButton>
      </header>

      <aside className="left-rail">
        <NeumorphicButton
          heightBox={60}
          widthBox={"100%"}
          borderRadius={12}
          label={"Posts"}
          onPress={() => navigate("/posts")}
          className="mb-s"
        />
        <NeumorphicButton
          heightBox={60}
          widthBox={"100%"}
          borderRadius={12}
          label={"Calendar"}
          onPress={() => navigate("/calendar")}
          className="mb-s"
        />
        <NeumorphicButton
          heightBox={60}
          widthBox={"100%"}
          borderRadius={12}
          label={"Requests"}
          onPress={() => navigate("/requests")}
          className="mb-s"
        />
        <NeumorphicButton
          heightBox={60}
          widthBox={"100%"}
          borderRadius={12}
          label={"Ratings"}
          onPress={() => navigate("/ratings")}
          className="mb-s"
        />
        <NeumorphicButton
          heightBox={60}
          widthBox={"100%"}
          borderRadius={12}
          label={"Events"}
          onPress={() => navigate("/events")}
          className="mb-s"
        />
        <NeumorphicButton
          heightBox={60}
          widthBox={"100%"}
          borderRadius={12}
          label={"Favorites"}
          onPress={() => navigate("/favorites")}
          className="mb-s"
        />

        <NeumorphicButton
          heightBox={60}
          widthBox={"100%"}
          borderRadius={12}
          hasIcons={true}
          label={"Companies"}
          color="black"
          leftIcon="organization"
          rightIcon="keyboard-arrow-right"
          onPress={() => navigate("/companies")}
          className="mb-s"
        />
        <NeumorphicButton
          heightBox={60}
          widthBox={"100%"}
          borderRadius={12}
          hasIcons={true}
          label={"Labor"}
          color="black"
          leftIcon="people"
          rightIcon="keyboard-arrow-right"
          onPress={() => navigate("/labor")}
          className="mb-s"
        />
        <NeumorphicButton
          heightBox={60}
          widthBox={"100%"}
          borderRadius={12}
          hasIcons={true}
          label={"Products"}
          color="black"
          leftIcon="package"
          rightIcon="keyboard-arrow-right"
          onPress={() => navigate("/products")}
          className="mb-s"
        />
        <NeumorphicButton
          heightBox={60}
          widthBox={"100%"}
          borderRadius={12}
          hasIcons={true}
          label={"Services"}
          color="black"
          leftIcon="tools"
          rightIcon="keyboard-arrow-right"
          onPress={() => navigate("/services")}
          className="mb-s"
        />
      </aside>

      <main className="right-content">
        <p>Content for the selected section will appear here.</p>
      </main>
    </div>
  );
};

export default Home;
